import { i18nBase } from 'services/Localization/i18n';

//
// Date helpers without a date library. The API exchanges ISO 8601 instants in UTC.
//

export function getDateTimeDiffHours(date1: Date, date2: Date): number {
  return Math.trunc((date1.getTime() - date2.getTime()) / 3600000);
}

export function addDateDays(date1: Date, days: number): Date {
  const result = new Date(date1.getTime());
  result.setDate(result.getDate() + days);

  return result;
}

// true when both dates fall on the same calendar day in the browser's local time
export function isSameLocalDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function toApiDateTimeOptional(date: Date | undefined): string | undefined {
  // convert the date object into a ISO 8601 date string in the UTC time zone
  if (!date) {
    return undefined;
  }

  return toApiDateTimeRequired(date);
}

export function toApiDateTimeRequired(date: Date): string {
  // convert the date object into a ISO 8601 date string in the UTC time zone
  return date.toISOString();
}

export function fromApiDateTimeOptional(dateString: string | undefined): Date | undefined {
  // convert the ISO 8601 date string in the UTC time zone to a date object in the local time zone
  if (!dateString) {
    return undefined;
  }

  return fromApiDateTimeRequired(dateString);
}

export function fromApiDateTimeRequired(dateString: string): Date {
  // convert the ISO 8601 date string in the UTC time zone to a date object in the local time zone
  return new Date(dateString);
}

// a calendar date for the API: the local year, month and day as UTC midnight (YYYY-MM-DDT00:00:00.000Z)
export function toApiDateRequired(date: Date): string {
  return `${toDateInputValue(date)}T00:00:00.000Z`;
}

// the local midnight of a calendar date the API stores as UTC midnight. The UTC components keep the same
// calendar day in every time zone: a local parse of "2026-08-03T00:00:00Z" west of UTC is August 2.
export function fromApiDateRequired(dateString: string): Date {
  const parsed = new Date(dateString);

  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 0, 0, 0, 0);
}

// The English day names of the Microsoft Graph recurrence format (daysOfWeek of a pattern).
export type WeekDay = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

// indexed by Date.getDay(): Sunday first
export const weekDays0: WeekDay[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// the display order of a week: Monday first
export const weekDays1: WeekDay[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// The week of the month of the Microsoft Graph recurrence format (index of a relative pattern).
export type WeekIndex = 'first' | 'second' | 'third' | 'fourth' | 'last';

// the week of the month the day of the date falls in: days 29 to 31 are the last week
export function getWeekIndex(date: Date): WeekIndex {
  const day = date.getDate();
  if (day <= 7) return 'first';
  if (day <= 14) return 'second';
  if (day <= 21) return 'third';
  if (day <= 28) return 'fourth';

  return 'last';
}

// formats a date and time in the current UI language; withSeconds for a record where the order within a minute counts
export function toLocaleDateTime(date?: Date, withSeconds: boolean = false): string {
  if (!date) return '';

  return date.toLocaleString(i18nBase.language || undefined, {
    dateStyle: 'medium',
    timeStyle: withSeconds ? 'medium' : 'short',
  });
}

// formats the time of day (hours and minutes) in the current UI language
export function toLocaleTime(date?: Date): string {
  if (!date) return '';

  return date.toLocaleTimeString(i18nBase.language || undefined, { hour: '2-digit', minute: '2-digit' });
}

const padDatePart = (value: number): string => value.toString().padStart(2, '0');

// the value of an <input type="date"> for a date in the browser's local time: YYYY-MM-DD, '' without a date
export function toDateInputValue(date?: Date): string {
  if (!date) return '';

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

// the local midnight of an <input type="date"> value; undefined for an empty or invalid value
export function fromDateInputValue(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

// the value of an <input type="datetime-local"> in the browser's local time: YYYY-MM-DDTHH:mm, '' without a date
export function toDateTimeInputValue(date?: Date): string {
  if (!date) return '';

  return `${toDateInputValue(date)}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
}

// the instant of an <input type="datetime-local"> value, read as local time; undefined for an empty or invalid value
export function fromDateTimeInputValue(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return undefined;

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
}

//The IANA zone the browser reports, such as Europe/Amsterdam. This is the only thing the client should
//send as a time zone: a UTC offset does not identify a zone.
export function getBrowserTimeZoneCode(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

// the zones offered where a browser cannot list its own (Intl.supportedValuesOf needs a 2022 engine)
const fallbackTimeZoneCodes: string[] = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Luxembourg',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Vienna',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Copenhagen',
  'Europe/Oslo',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
];

//The IANA zones to choose from, sorted, with UTC and the given extra zones (such as the browser's and a stored
//one) added when the browser's list leaves them out.
export function getTimeZoneCodes(extraCodes: (string | undefined)[] = []): string[] {
  let codes: string[];
  try {
    codes = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : fallbackTimeZoneCodes;
  } catch {
    codes = fallbackTimeZoneCodes;
  }

  const all = new Set<string>(codes);
  ['UTC', ...extraCodes].forEach((code) => {
    if (code) all.add(code);
  });

  return [...all].sort((a, b) => a.localeCompare(b));
}
